import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import {
	type MediaAssetDto,
	type MediaType,
	PermissionService,
	RbacActions,
} from '@supply-mover-2-0/shared';
import React, { useEffect, useState } from 'react';

import { useTranslation } from '../../providers/TranslationProvider';
import { Icon } from '../base/Icon';
import { LoadingSpinner } from '../display/LoadingSpinner';
import { Callout } from '../feedback/Callout';
import { Modal } from '../overlays/Modal';
import { MediaBrowser } from './MediaBrowser';
import { describeMediaDimensions, MEDIA_TYPE_LABELS } from './mediaFormat';
import { useMediaLibraryData } from './useMediaLibraryData';

/** Where the Media Library screen lives; the picker links to an item there. */
export const MEDIA_LIBRARY_PATH = '/marketing/media';

export interface MediaPickerModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Called with the chosen, ready item. The modal closes itself afterwards. */
	onChoose: (asset: MediaAssetDto) => void;
	title?: string;
	/** What the slot takes; a logo slot might take `['svg', 'image']`. Default: pictures and SVGs. */
	allowedTypes?: ReadonlyArray<MediaType>;
	/** The brand whose items to show first (e.g. the website's brand). */
	brandId?: number | null;
}

const DEFAULT_TYPES: ReadonlyArray<MediaType> = ['image', 'svg'];

/**
 * PD-16339 — choose an item from the media library for a slot (a website picture, an
 * email image, a promotion banner). People can upload into the library from here
 * without leaving what they were doing, and open an item in the Media Library in a new
 * tab to edit it.
 */
export function MediaPickerModal({
	isOpen,
	onClose,
	onChoose,
	title,
	allowedTypes = DEFAULT_TYPES,
	brandId,
}: MediaPickerModalProps): React.ReactElement {
	const { t } = useTranslation();
	const library = useMediaLibraryData(isOpen);
	const [selected, setSelected] = useState<MediaAssetDto | null>(null);
	const canUpload = PermissionService.can(RbacActions.MEDIA.UPLOAD);

	useEffect(() => {
		if (isOpen) {
			setSelected(null);
		}
	}, [isOpen]);

	const choose = (asset: MediaAssetDto | null): void => {
		if (asset == null || asset.status !== 'ready') {
			return;
		}
		onChoose(asset);
		onClose();
	};

	const dimensions =
		selected == null ? null : describeMediaDimensions(selected);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size="full"
			title={title ?? t('Choose from the media library')}
			actions={[
				{ label: t('Cancel'), onClick: onClose, variant: 'outline' },
				{
					label: t('Use this'),
					onClick: (): void => {
						choose(selected);
					},
					variant: 'primary',
					disabled: selected == null || selected.status !== 'ready',
				},
			]}
		>
			{library.error != null && (
				<Callout variant="danger">{t(library.error)}</Callout>
			)}
			{library.error == null && library.access == null && (
				<div className="flex justify-center py-10">
					<LoadingSpinner label={t('Loading media...')} showLabel />
				</div>
			)}
			{library.access != null && (
				<div className="flex flex-col gap-3">
					<MediaBrowser
						library={library}
						allowedTypes={allowedTypes}
						canUpload={canUpload}
						readyOnly
						pageSize={24}
						initialBrand={brandId == null ? undefined : brandId}
						selectedId={selected?.id ?? null}
						onSelect={setSelected}
						onActivate={choose}
						onUploaded={(assets) => {
							// One file uploaded here is almost always the one wanted.
							const [only] = assets;
							if (assets.length === 1 && only != null) {
								setSelected(only);
							}
						}}
						gridClassName="max-h-[50vh] overflow-y-auto pr-1"
					/>
					<div className="flex min-h-[2.5rem] flex-wrap items-center justify-between gap-2 border-t border-muted pt-3">
						<p className="text-sm text-muted">
							{selected == null
								? t('Select an item, or double-click to use it straight away.')
								: `${t(selected.title)} · ${t(MEDIA_TYPE_LABELS[selected.mediaType])}${
										dimensions != null ? ` · ${t(dimensions)}` : ''
									}`}
						</p>
						{selected != null && (
							<a
								href={`${MEDIA_LIBRARY_PATH}?item=${selected.id}`}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
							>
								{t('Open in Media Library')}
								<Icon icon={faArrowUpRightFromSquare} size="xs" />
							</a>
						)}
					</div>
				</div>
			)}
		</Modal>
	);
}
